import { getCategories } from '../config/runtimeListingConfig';

/**
 * Category Landing Page Helpers
 *
 * These utilities handle URL resolution, breadcrumb generation, and category lookups
 * for the category landing pages feature.
 *
 * URL Strategy:
 * - Every category uses a single-segment URL: /{url_slug}
 * - url_slugs are unique across the entire category tree, so no nesting in the URL is needed
 */

/**
 * Build a flat lookup map from slug to category with full path information
 * @param {Array} cats - Categories array (defaults to imported categories)
 * @returns {Object} Map of { slug: { category, path: [ancestor IDs], parentSlug } }
 */
export function buildCategorySlugMap(cats = getCategories()) {
  const slugMap = {};

  const traverse = (categoryList, path = [], parentSlug = null, parentLevel = 0) => {
    categoryList.forEach(cat => {
      const currentPath = [...path, cat.id];

      slugMap[cat.url_slug] = {
        category: cat,
        path: currentPath,
        parentSlug,
        level: cat.level || parentLevel + 1,
      };

      if (cat.subcategories && cat.subcategories.length > 0) {
        traverse(cat.subcategories, currentPath, cat.url_slug, cat.level || parentLevel + 1);
      }
    });
  };

  traverse(cats);
  return slugMap;
}

/**
 * Build a map from category ID to category with path information
 * @param {Array} cats - Categories array
 * @returns {Object} Map of { id: { category, path: [ancestor IDs] } }
 */
export function buildCategoryIdMap(cats = getCategories()) {
  const idMap = {};

  const traverse = (categoryList, path = []) => {
    categoryList.forEach(cat => {
      const currentPath = [...path, cat.id];

      idMap[cat.id] = {
        category: cat,
        path: currentPath,
      };

      if (cat.subcategories && cat.subcategories.length > 0) {
        traverse(cat.subcategories, currentPath);
      }
    });
  };

  traverse(cats);
  return idMap;
}


/**
 * Resolve a URL slug to a category.
 * Since all url_slugs are unique across the entire tree, a single slug always
 * identifies exactly one category regardless of its level.
 *
 * @param {Array<string>} slugs - URL segments — only the first segment is used
 * @param {Array} cats - Categories array
 * @returns {Object|null} { category, breadcrumbPath: [...categories] } or null
 */
export function resolveCategoryFromUrl(slugs, cats = getCategories()) {
  if (!slugs || slugs.length === 0) return null;

  const slugMap = buildCategorySlugMap(cats);
  const idMap = buildCategoryIdMap(cats);

  const info = slugMap[slugs[0]];
  if (!info) return null;

  const breadcrumbPath = info.path.map(id => idMap[id]?.category).filter(Boolean);
  return { category: info.category, breadcrumbPath };
}

/**
 * Get the full breadcrumb path for a category by ID
 * @param {string} categoryId - The category ID
 * @param {Array} cats - Categories array
 * @returns {Array} Array of category objects from root to target
 */
export function getCategoryBreadcrumbPath(categoryId, cats = getCategories()) {
  const idMap = buildCategoryIdMap(cats);
  const info = idMap[categoryId];

  if (!info) return [];

  return info.path.map(id => idMap[id]?.category).filter(Boolean);
}

/**
 * Get the URL for a category.
 * Always /{url_slug} — slugs are unique across the entire tree.
 *
 * @param {string} categoryId - The category ID
 * @param {Array} cats - Categories array
 * @returns {string|null} The URL path or null
 */
export function getCategoryUrl(categoryId, cats = getCategories()) {
  const idMap = buildCategoryIdMap(cats);
  const info = idMap[categoryId];
  if (!info) return null;
  return `/${info.category.url_slug}`;
}

/**
 * Get category filter params for listing queries
 * Maps category to the appropriate categoryLevel1-4 params
 *
 * @param {Object} category - The category object
 * @param {Array} breadcrumbPath - Array of ancestor categories
 * @returns {Object} Filter params like { pub_categoryLevel1: 'wind_reed', pub_categoryLevel2: 'flute' }
 */
export function getCategoryFilterParams(category, breadcrumbPath) {
  const params = {};

  breadcrumbPath.forEach((cat, index) => {
    const levelKey = `pub_categoryLevel${index + 1}`;
    params[levelKey] = cat.id;
  });

  return params;
}

